/**
 * Groups order detail items by ModifierRelationID.
 * Items with the same ModifierRelationID belong together: main product, variants, modifiers, and item-level discounts.
 *
 * DB default for ModifierRelationID is '0' — that is NOT a real group key
 * (same rule as backend IsMeaningfulModifierRelationId).
 *
 * @param {Array} orderDetails - Array of order detail items from API
 * @returns {Array} Array of grouped items: { main, variants, modifiers, discounts }
 */

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

function isMeaningfulModifierRelationId(relationId) {
  if (relationId == null) return false;
  const trimmed = String(relationId).trim();
  return trimmed !== "" && trimmed !== "0";
}

export function groupOrderItemsByModifierRelation(orderDetails) {
  if (!orderDetails || !Array.isArray(orderDetails)) return [];

  const groups = new Map();

  const getType = (item) =>
    (item.menuItem_Type || item.MenuItem_Type || "").toUpperCase();
  const getModifierRelationID = (item) =>
    item.modifierRelationID || item.ModifierRelationID || null;
  const getDetailId = (item) => {
    const raw =
      item.recvorderDetail_ID ?? item.RecvorderDetail_ID ?? item.id ?? null;
    if (raw == null || raw === "") return null;
    const s = String(raw).trim();
    if (!s || s === EMPTY_GUID) return null;
    return s;
  };

  let standaloneSeq = 0;
  for (const item of orderDetails) {
    const relationId = getModifierRelationID(item);
    const detailId = getDetailId(item);
    const key = isMeaningfulModifierRelationId(relationId)
      ? String(relationId).trim()
      : `standalone_${detailId ?? `row_${standaloneSeq++}`}`;

    if (!groups.has(key)) {
      groups.set(key, { main: null, variants: [], modifiers: [], discounts: [] });
    }

    const group = groups.get(key);
    const type = getType(item);

    switch (type) {
      case "VARIANT":
        group.variants.push(item);
        break;
      case "MODIFIER":
        group.modifiers.push(item);
        break;
      case "DISCOUNT":
        group.discounts.push(item);
        break;
      default:
        // Main item: PRODUCT, COMBO, etc.
        if (!group.main) {
          group.main = item;
        } else {
          group.modifiers.push(item);
        }
        break;
    }
  }

  return Array.from(groups.values()).filter((g) => g.main != null);
}
